import { AuthContext } from 'providers/authProvider';
import React from 'react';
import { Upload } from 'react-feather';
import { hasPermission } from 'services/auth';
import { checkDuplicatePropertyValues } from 'utilities';

import { Button, NOTIFICATION_TYPE, Notification } from 'components';
import { useCreateBuyers } from 'features/buyers/hooks/useCreateBuyers';
import { CsvImportModal } from 'features/common/csv/csv-import/CsvImportModal';

import { importBuyersValidationSchema } from '../form/BuyerForm.validator';

export function BuyersCsvImport({ columns, onCsvImportSuccess }) {
  const { user } = React.useContext(AuthContext);

  const [showCsvImportModal, setShowCsvImportModal] = React.useState(false);

  const { mutate: createBuyers, isLoading } = useCreateBuyers('batch', {
    onSuccess: () => {
      Notification(NOTIFICATION_TYPE.SUCCESS, 'Import buyers successfully');
      setShowCsvImportModal(false);
      onCsvImportSuccess();
    },
    onError: (err) => {
      Notification(NOTIFICATION_TYPE.DANGER, err.message);
    },
  });

  const fields = columns.map((column) => ({
    title: column.title,
    dataIndex: column.dataIndex,
    isRequired: !!importBuyersValidationSchema.fields[column.dataIndex]?.exclusiveTests?.required,
  }));

  const handleSubmitImport = (rowsData) => {
    const fields = columns.map((column) => ({
      label: column.title,
      key: column.dataIndex,
    }));

    const data = rowsData.map((row) => {
      const rowData = {};

      fields.forEach((field) => {
        rowData[field.key] = row[field.label];
      });

      return rowData;
    });

    const transformedData = data.map((item) => ({
      ...item,
      address: {
        street: item.street1,
        city: item.city1,
        state: item.state1,
        zip: item.zip1,
        country: item.country1,
      },
    }));

    // Check for duplicate company names or emails
    if (checkDuplicatePropertyValues(transformedData, 'companyName')) {
      return Notification(NOTIFICATION_TYPE.DANGER, 'Duplicate company names are not allowed');
    }

    if (checkDuplicatePropertyValues(transformedData, 'email')) {
      return Notification(NOTIFICATION_TYPE.DANGER, 'Duplicate email addresses are not allowed');
    }

    transformedData.forEach((item) => {
      delete item.id;
      delete item.street1;
      delete item.city1;
      delete item.state1;
      delete item.zip1;
      delete item.country1;
    });

    createBuyers(transformedData);
  };

  return (
    <>
      <Button
        className="d-flex gap-2"
        disabled={!hasPermission(user, { resource: 'buyers', action: 'create' })}
        onClick={() => setShowCsvImportModal(true)}
      >
        <Upload size={20} /> Import CSV
      </Button>
      {showCsvImportModal && (
        <CsvImportModal
          title="Import Buyers CSV"
          open={showCsvImportModal}
          onClose={() => setShowCsvImportModal(false)}
          isSubmitting={isLoading}
          onSubmit={handleSubmitImport}
          fields={fields}
        />
      )}
    </>
  );
}
